import { assetUrl, http, isOfflineError, readCached, writeCached } from "./http";
import { enqueue, registerProcessor, replayOptions } from "@/offline/outbox";

/* -------------------------------------------------------------------------- */
/* Périodes et barème                                                          */
/* -------------------------------------------------------------------------- */

export interface GradingPeriod {
  id: string;
  schoolYear: string;
  position: number;
  name: string;
  startDate: string;
  endDate: string;
  isLocked: boolean;
  lockedAt: string | null;
  lockedByName: string | null;
}

export interface GradeScale {
  cycleId: string;
  label: string;
  gradeScale: number;
  passingAverage: number;
}

type ApiPeriod = Omit<GradingPeriod, "id"> & { id: number };
const toPeriod = (p: ApiPeriod): GradingPeriod => ({ ...p, id: String(p.id) });

export async function getPeriods(schoolYear?: string): Promise<GradingPeriod[]> {
  return (await http.get<ApiPeriod[]>("/grading-periods", schoolYear ? { schoolYear } : {})).map(toPeriod);
}

export async function generatePeriods(schoolYear: string, mode: "TRIMESTERS" | "SEMESTERS"): Promise<GradingPeriod[]> {
  return (await http.post<ApiPeriod[]>("/grading-periods/generate", { schoolYear, mode })).map(toPeriod);
}

export async function updatePeriod(
  id: string,
  patch: Partial<Pick<GradingPeriod, "name" | "startDate" | "endDate">>,
): Promise<GradingPeriod> {
  return toPeriod(await http.put<ApiPeriod>(`/grading-periods/${id}`, patch));
}

export async function setPeriodLocked(id: string, locked: boolean): Promise<GradingPeriod> {
  return toPeriod(await http.post<ApiPeriod>(`/grading-periods/${id}/${locked ? "lock" : "unlock"}`, {}));
}

export async function getGradeScales(): Promise<GradeScale[]> {
  const cycles = await http.getAll<{ id: number; label: string; gradeScale: number; passingAverage: number }>("/cycles");
  return cycles.map((c) => ({ cycleId: String(c.id), label: c.label, gradeScale: c.gradeScale, passingAverage: c.passingAverage }));
}

export async function updateGradeScale(cycleId: string, gradeScale: number, passingAverage: number): Promise<void> {
  await http.put(`/grading-periods/scales/${cycleId}`, { gradeScale, passingAverage });
}

/* -------------------------------------------------------------------------- */
/* Évaluations et notes                                                        */
/* -------------------------------------------------------------------------- */

export type GradeStatus = "PRESENT" | "ABSENT" | "EXEMPT";

export interface SubjectProgress {
  subjectId: string;
  name: string;
  coefficient: number;
  /** Élèves notés (ou absents, dispensés) dans cette matière. */
  graded: number;
}

/** Évaluation d'une classe (ex. « Évaluation d'octobre ») : elle porte sur toutes les matières de la classe. */
export interface Evaluation {
  id: string;
  periodId: string;
  classId: string;
  title: string;
  date: string;
  /** Toutes les matières sont notées sur cette valeur (le barème du cycle). */
  maxScore: number;
  createdByName: string;
  studentCount: number;
  subjects: SubjectProgress[];
}

export interface GradeRow {
  enrollmentId: string;
  studentId: string;
  matricule: string;
  lastName: string;
  firstName: string;
  score: number | null;
  /** Vide : pas encore noté. */
  status: GradeStatus | null;
}

export interface EvaluationGrades {
  evaluation: Evaluation;
  subjectId: string;
  periodLocked: boolean;
  canEdit: boolean;
  average: number | null;
  rows: GradeRow[];
  /** Enregistrées sur l'appareil (hors ligne), pas encore envoyées au serveur. */
  queued?: boolean;
}

export interface Teachable {
  /** Saisie possible dans toutes les classes. */
  all: boolean;
  assignments: Array<{ classId: string; className: string; cycleId: string; subjectIds: string[]; allSubjects: boolean }>;
}

const toEvaluation = (e: any): Evaluation => ({
  ...e,
  id: String(e.id),
  periodId: String(e.periodId),
  classId: String(e.classId),
  subjects: (e.subjects ?? []).map((s: any) => ({ ...s, subjectId: String(s.subjectId) })),
});

const toGrades = (g: any): EvaluationGrades => ({
  ...g,
  evaluation: toEvaluation(g.evaluation),
  subjectId: String(g.subjectId),
  rows: g.rows.map((r: any) => ({ ...r, enrollmentId: String(r.enrollmentId), studentId: String(r.studentId) })),
});

export async function getTeachable(): Promise<Teachable> {
  const t = await http.get<{
    all: boolean;
    assignments: Array<{ classId: number; className: string; cycleId: number; subjectIds: number[]; allSubjects: boolean }>;
  }>("/evaluations/teachable");
  return {
    all: t.all,
    assignments: t.assignments.map((a) => ({
      classId: String(a.classId),
      className: a.className,
      cycleId: String(a.cycleId),
      subjectIds: a.subjectIds.map(String),
      allSubjects: a.allSubjects,
    })),
  };
}

/** L'utilisateur peut-il saisir les notes de cette matière dans cette classe ? */
export function canTeach(teachable: Teachable | null, classId: string, subjectId: string): boolean {
  if (!teachable) return false;
  if (teachable.all) return true;
  const entry = teachable.assignments.find((a) => a.classId === classId);
  return !!entry && (entry.allSubjects || entry.subjectIds.includes(subjectId));
}

export async function getEvaluations(periodId: string, classId: string): Promise<Evaluation[]> {
  return (await http.get<any[]>("/evaluations", { periodId, classId })).map(toEvaluation);
}

export async function createEvaluation(input: { periodId: string; classId: string; title: string; date: string }): Promise<Evaluation> {
  return toEvaluation(
    await http.post<any>("/evaluations", { ...input, periodId: Number(input.periodId), classId: Number(input.classId) }),
  );
}

export async function updateEvaluation(id: string, patch: { title?: string; date?: string }): Promise<Evaluation> {
  return toEvaluation(await http.put<any>(`/evaluations/${id}`, patch));
}

export async function deleteEvaluation(id: string): Promise<void> {
  await http.delete(`/evaluations/${id}`);
}

export async function getEvaluationGrades(id: string, subjectId: string): Promise<EvaluationGrades> {
  return toGrades(await http.get<any>(`/evaluations/${id}/grades`, { subjectId }));
}

interface GradesPayload {
  evaluationId: string;
  body: { subjectId: number; grades: Array<{ enrollmentId: number; score: number | null; status: GradeStatus }> };
}

/**
 * Enregistre les notes d'une matière. Hors ligne, elles sont gardées sur l'appareil (et visibles à la
 * réouverture) puis envoyées au retour du réseau ; une nouvelle saisie de la même matière remplace l'attente.
 */
export async function saveEvaluationGrades(
  id: string,
  subjectId: string,
  grades: Array<{ enrollmentId: string; score: number | null; status: GradeStatus }>,
): Promise<EvaluationGrades> {
  const body = {
    subjectId: Number(subjectId),
    grades: grades.map((g) => ({ enrollmentId: Number(g.enrollmentId), score: g.score, status: g.status })),
  };
  try {
    return toGrades(await http.put<any>(`/evaluations/${id}/grades`, body));
  } catch (error) {
    if (!isOfflineError(error)) throw error;
    const cached = await readCached<any>(`/evaluations/${id}/grades`, { subjectId });
    if (!cached) throw error;
    const byEnrollment = new Map(body.grades.map((g) => [g.enrollmentId, g]));
    const rows = cached.rows.map((r: any) => {
      const g = byEnrollment.get(Number(r.enrollmentId));
      return g ? { ...r, score: g.score, status: g.status } : r;
    });
    const scores = rows.filter((r: any) => r.status === "PRESENT" && r.score !== null).map((r: any) => Number(r.score));
    const local = {
      ...cached,
      rows,
      average: scores.length ? Math.round((scores.reduce((a: number, b: number) => a + b, 0) / scores.length) * 100) / 100 : null,
    };
    const subject = cached.evaluation?.subjects?.find((s: any) => String(s.subjectId) === subjectId)?.name ?? "matière";
    await enqueue({
      kind: "grades",
      label: `Notes — ${cached.evaluation?.title ?? "évaluation"}, ${subject}`,
      payload: { evaluationId: id, body } satisfies GradesPayload,
      replaceKey: `grades:${id}:${subjectId}`,
    });
    await writeCached(`/evaluations/${id}/grades`, { subjectId }, local);
    return { ...toGrades(local), queued: true };
  }
}

registerProcessor("grades", async (item) => {
  const { evaluationId, body } = item.payload as GradesPayload;
  await http.put(`/evaluations/${evaluationId}/grades`, body, replayOptions(item));
  return { done: true };
});

/* -------------------------------------------------------------------------- */
/* Bulletins                                                                   */
/* -------------------------------------------------------------------------- */

export interface ReportSubject {
  subjectId: string;
  name: string;
  coefficient: number;
  classAverage: number | null;
}

export interface StudentResult {
  enrollmentId: string;
  studentId: string;
  matricule: string;
  lastName: string;
  firstName: string;
  gender: string | null;
  birthDate: string | null;
  photoUrl: string | undefined;
  subjects: Array<{
    subjectId: string;
    /** Note de chaque évaluation (dans l'ordre de `ClassReport.evaluations`), sur le barème. */
    scores: Array<number | null>;
    /** Moyenne de la matière : moyenne des évaluations (ou la note de l'évaluation). */
    average: number | null;
    points: number | null;
    count: number;
    mention: string | null;
  }>;
  /** Moyenne générale à chaque évaluation. */
  evaluationAverages: Array<number | null>;
  totalPoints: number | null;
  average: number | null;
  rank: number | null;
  rankTied: boolean;
  mention: string | null;
  remark: string | null;
}

export interface ClassReport {
  /** Bulletin de la période (toutes ses évaluations) ou d'une seule évaluation. */
  scope: "PERIOD" | "EVALUATION";
  period: GradingPeriod;
  evaluations: Array<{ id: string; title: string; date: string }>;
  classId: string;
  className: string;
  cycleId: string;
  gradeScale: number;
  passingAverage: number;
  titularName: string | null;
  subjects: ReportSubject[];
  students: StudentResult[];
  stats: { count: number; graded: number; passed: number; average: number | null; highest: number | null; lowest: number | null };
}

export interface AnnualStudent {
  enrollmentId: string;
  studentId: string;
  matricule: string;
  lastName: string;
  firstName: string;
  periodAverages: Array<number | null>;
  average: number | null;
  rank: number | null;
  rankTied: boolean;
  mention: string | null;
  decision: "ADMIS" | "REDOUBLE" | null;
}

export interface AnnualResults {
  schoolYear: string;
  classId: string;
  className: string;
  gradeScale: number;
  passingAverage: number;
  periods: GradingPeriod[];
  final: boolean;
  students: AnnualStudent[];
  stats: { count: number; admitted: number; repeating: number; average: number | null };
}

/** Résultats de la classe pour la période, ou pour une seule évaluation (`evaluationId`). */
export async function getClassReport(periodId: string, classId: string, evaluationId?: string): Promise<ClassReport> {
  const r = await http.get<any>("/report-cards", { periodId, classId, ...(evaluationId ? { evaluationId } : {}) });
  return {
    ...r,
    period: toPeriod(r.period),
    evaluations: r.evaluations.map((e: any) => ({ ...e, id: String(e.id) })),
    classId: String(r.classId),
    cycleId: String(r.cycleId),
    subjects: r.subjects.map((s: any) => ({ ...s, subjectId: String(s.subjectId) })),
    students: r.students.map((s: any) => ({
      ...s,
      enrollmentId: String(s.enrollmentId),
      studentId: String(s.studentId),
      photoUrl: assetUrl(s.photoUrl),
      subjects: s.subjects.map((x: any) => ({ ...x, subjectId: String(x.subjectId) })),
    })),
  };
}

export async function getAnnualResults(classId: string, schoolYear?: string): Promise<AnnualResults> {
  const r = await http.get<any>("/report-cards/annual", { classId, ...(schoolYear ? { schoolYear } : {}) });
  return {
    ...r,
    classId: String(r.classId),
    periods: r.periods.map(toPeriod),
    students: r.students.map((s: any) => ({ ...s, enrollmentId: String(s.enrollmentId), studentId: String(s.studentId) })),
  };
}

export async function saveRemark(periodId: string, enrollmentId: string, remark: string): Promise<void> {
  await http.put("/report-cards/remarks", { periodId: Number(periodId), enrollmentId: Number(enrollmentId), remark });
}

/** Note affichée à la française : 13,5. */
export const formatScore = (value: number | null | undefined): string =>
  value === null || value === undefined ? "—" : value.toLocaleString("fr-FR", { maximumFractionDigits: 2 });

/** Rang affiché : « 1er », « 1re », « 2e », « 2e ex » (ex æquo). */
export const formatRank = (rank: number | null, tied = false, gender?: string | null): string =>
  rank === null ? "—" : `${rank === 1 ? (gender === "F" ? "1re" : "1er") : `${rank}e`}${tied ? " ex" : ""}`;

/** Titre d'un bulletin : « 1er trimestre » ou « Évaluation d'octobre — 1er trimestre ». */
export const reportTitle = (report: ClassReport): string =>
  report.scope === "EVALUATION" ? `${report.evaluations[0]?.title ?? "Évaluation"} — ${report.period.name}` : report.period.name;
